import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation"
import { Suggestion } from "@/components/ai-elements/suggestion"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { LogoMark } from "@/components/common/Logo/LogoMark"
import { useAgentChatContext } from "@/contexts/AgentChatContext"
import { useChatContext } from "@/contexts/ChatContext"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { ActionMessage } from "@/features/agent-chat/components/ActionMessage"
import { ProposalCard } from "@/features/agent-chat/components/ProposalCard"
import { useAgentChatEnabled } from "@/features/agent-chat/useAgentChatEnabled"
import {
  AI_CHAT_MOCK_QUESTION_KEY,
  AI_CHAT_MOCK_RESPONSE_KEY,
  useOnboarding,
} from "@/features/onboarding"
import {
  TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router"
import { useStickToBottomContext } from "use-stick-to-bottom"
import { ChatInput } from "./ChatInput"
import { ChatMessageItem } from "./ChatMessageItem"
import { StreamingMessage } from "./StreamingMessage"
import { useChatActions } from "./hooks/useChatActions"
import { useChatSubmit } from "./hooks/useChatSubmit"
import { useConversationRouting } from "./hooks/useConversationRouting"

const CHAT_SURFACE_WIDTH_CLASS = "mx-auto w-full max-w-[1200px]"

const SUGGESTION_KEYS = [
  "AI_CHAT/SUGGESTIONS/ONE",
  "AI_CHAT/SUGGESTIONS/TWO",
  "AI_CHAT/SUGGESTIONS/THREE",
] as const

const MC_SUGGESTION_KEYS = [
  "AI_CHAT/SUGGESTIONS/MC_ONE",
  "AI_CHAT/SUGGESTIONS/MC_TWO",
  "AI_CHAT/SUGGESTIONS/MC_THREE",
] as const

const QueueScrollSync: React.FC<{ queuedMessageCount: number }> = ({
  queuedMessageCount,
}) => {
  const { scrollToBottom } = useStickToBottomContext()
  const previousQueuedMessageCountRef = useRef(queuedMessageCount)

  useEffect(() => {
    if (queuedMessageCount > previousQueuedMessageCountRef.current) {
      void scrollToBottom({ wait: 100 })
    }

    previousQueuedMessageCountRef.current = queuedMessageCount
  }, [queuedMessageCount, scrollToBottom])

  return null
}

/**
 * The confirmation bar grows the composer upward, which eats the bottom of the
 * scroll area — so the reply explaining what is about to be confirmed ends up
 * hidden behind the very card asking about it. Scroll it back into view when
 * the card appears.
 *
 * Lives inside <Conversation> because that is where the stick-to-bottom
 * context is; the card itself renders in the composer, outside it.
 */
const ConfirmationScrollSync: React.FC<{ isAwaitingConfirmation: boolean }> = ({
  isAwaitingConfirmation,
}) => {
  const { scrollToBottom } = useStickToBottomContext()
  // Starts false even when a confirmation is already open, so a surface
  // mounted with one pending (switching between the docked panel and the full
  // page mid-turn) scrolls it into view too, rather than only reacting to a
  // card that appears later.
  const wasAwaitingRef = useRef(false)

  useEffect(() => {
    if (isAwaitingConfirmation && !wasAwaitingRef.current) {
      // `wait` lets the composer finish its expand animation first, so the
      // scroll target accounts for the card's final height rather than zero.
      void scrollToBottom({ wait: 250 })
    }
    wasAwaitingRef.current = isAwaitingConfirmation
  }, [isAwaitingConfirmation, scrollToBottom])

  return null
}

interface ChatInterfaceProps {
  /**
   * Set to false when rendering outside the /chat route (e.g. in the nav
   * header chat sheet) so the active conversation isn't synced to the host
   * page's URL. Defaults to true for the standalone /chat page.
   */
  syncUrl?: boolean
  /**
   * Tight layout for narrow containers (e.g. the nav-header chat sheet)
   * where Tailwind's `md:` breakpoint is unreliable — it reacts to the
   * browser window's width, not this component's own container width.
   */
  compact?: boolean
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  syncUrl = true,
  compact = false,
}) => {
  const {
    chat,
    setChat,
    streamingMessage,
    setStreamingMessage,
    isLoading,
    setIsLoading,
  } = useChatContext()
  const { translate, currentLanguage } = useTranslation()
  const webSearchEnabled = chat.webSearchEnabled ?? false
  const { activeTourId, currentStepIndex, getCurrentTour } = useOnboarding()

  const { pathname } = useLocation()
  const isMaterialComplianceRoute = pathname.startsWith("/material-compliance")
  const materialComplianceEnabled = useFeatureFlag("MATERIAL_COMPLIANCE")
  const suggestionKeys =
    isMaterialComplianceRoute && materialComplianceEnabled
      ? MC_SUGGESTION_KEYS
      : SUGGESTION_KEYS

  // Check if we're on AI chat tour steps that need mock data
  const currentTour = getCurrentTour()
  const isOnThinkingStep =
    activeTourId === "ai-chat" &&
    currentTour?.steps[currentStepIndex]?.id === "chat-thinking-step"
  const isOnResponseStep =
    activeTourId === "ai-chat" &&
    currentTour?.steps[currentStepIndex]?.id === "chat-response-step"
  const showMockContent = isOnThinkingStep || isOnResponseStep

  const agentChatEnabled = useAgentChatEnabled()
  const agentChat = useAgentChatContext()

  const {
    activeConversationId,
    isHydratingConversation,
    clearActiveConversationFromUrl,
  } = useConversationRouting({
    chat,
    setChat,
    translate,
    syncUrl,
    onConversationSwitch: agentChat.resetForNewConversation,
  })

  const { exportChat, exportChatPdf, copyToClipboard, startNewChat } =
    useChatActions({
      chat,
      setChat,
      translate,
      currentLanguage,
      onStartNewChat: () => {
        clearActiveConversationFromUrl()
        agentChat.resetForNewConversation()
      },
    })

  // Actions/proposals/tool-call handlers post fallback and failure notices as
  // standalone assistant messages; this is how they reach whichever chat
  // surface (docked/floating/full-page) is currently mounted.
  useEffect(() => {
    agentChat.registerAppendAssistantMessage((text) => {
      setChat((prev) => ({
        ...prev,
        messages: [
          ...(prev.messages ?? []),
          { role: "assistant", content: text },
        ],
        updated_at: new Date().toISOString(),
      }))
    })
  }, [agentChat, setChat])

  const {
    handleSubmit,
    queuedMessages,
    removeQueuedMessage,
    forceSendQueuedMessage,
  } = useChatSubmit({
    chat,
    activeConversationId,
    setChat,
    setStreamingMessage,
    setIsLoading,
    translate,
    agentChatEnabled,
    pageContext: agentChatEnabled ? agentChat.effectivePageContext : undefined,
    uiLanguage: currentLanguage,
    onAction: agentChatEnabled ? agentChat.handleAction : undefined,
    executeToolCall: agentChatEnabled ? agentChat.executeToolCall : undefined,
    onTurnEnd: agentChatEnabled ? agentChat.flushQueuedNavigations : undefined,
  })

  const handleToggleWebSearch = (value: boolean) => {
    setChat((prev) => ({
      ...prev,
      webSearchEnabled: value,
    }))
  }

  const [inputPrefill, setInputPrefill] = useState<{
    text: string
    id: number
  } | null>(null)

  const handleSuggestionClick = (suggestion: string) => {
    setInputPrefill((previous) => ({
      text: suggestion,
      id: (previous?.id ?? 0) + 1,
    }))
  }

  // Agent-loop continuation messages carry raw tool_use/tool_result blocks
  // (toolContent) with no visible content — excluded before anchors below
  // are compared, so a card's anchorMessageCount lines up with this list's
  // indices, not the raw chat.messages indices.
  //
  // Also excluded: the committed message for the turn CURRENTLY streaming,
  // when there is one. A turn spanning a blocking-tool continuation commits
  // its first round into chat.messages, then the next round's streaming
  // bubble republishes that same round's content again (the accumulator is
  // cumulative) — rendering both would show it twice until the turn
  // finishes. The streaming bubble is the fuller, more current copy, so the
  // committed one steps aside for as long as they'd overlap; it reappears
  // (now fully updated) the instant streamingMessage clears.
  const visibleMessages = useMemo(
    () =>
      chat.messages?.filter(
        (message) =>
          !message.toolContent &&
          !(streamingMessage?.turnId && message.turnId === streamingMessage.turnId),
      ) ?? [],
    [chat.messages, streamingMessage?.turnId],
  )
  // Clamp each card's anchor into [0, visibleMessages.length] so a stale or
  // unanchored (Infinity) value still renders at the end instead of vanishing.
  const anchoredPendingActions = useMemo(
    () =>
      agentChat.pendingActions.map((action) => ({
        ...action,
        anchorSlot: Math.min(action.anchorMessageCount, visibleMessages.length),
      })),
    [agentChat.pendingActions, visibleMessages.length],
  )
  const anchoredProposals = useMemo(
    () =>
      agentChat.proposals.map((proposal) => ({
        ...proposal,
        anchorSlot: Math.min(
          proposal.anchorMessageCount,
          visibleMessages.length,
        ),
      })),
    [agentChat.proposals, visibleMessages.length],
  )

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.14)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.10)_1px,transparent_1px)] bg-size-[72px_72px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-linear-to-b from-grey1/55 via-grey2/15 to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_55%_at_50%_-10%,hsl(var(--primary)/0.07),transparent_70%)]"
      />
      <Conversation className="relative z-10 flex-1">
        <ConversationContent
          className={
            chat.messages?.length === 0
              ? `${CHAT_SURFACE_WIDTH_CLASS} h-full ${compact ? "px-XS" : "px-XXL"}`
              : `${CHAT_SURFACE_WIDTH_CLASS} ${compact ? "px-XS" : "px-XXL"}`
          }
        >
          {chat.messages?.length === 0 && !showMockContent ? (
            isHydratingConversation ? (
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner size={28} />
              </div>
            ) : (
              <ConversationEmptyState className="mx-auto max-w-xl">
                <div className="relative flex size-16 items-center justify-center rounded-2xl bg-grey1/40 text-primary shadow-[0_0_48px_hsl(var(--primary)/0.18)] backdrop-blur-xs">
                  <LogoMark className="size-9" />
                </div>
                <h3 className="text-sm font-medium">
                  {translate("AI_CHAT/INTRO_ONE") +
                    translate("AI_CHAT/INTRO_TWO")}
                </h3>
                <div className="mt-2 flex flex-col items-center gap-2">
                  {suggestionKeys.map((suggestionKey) => (
                    <Suggestion
                      key={suggestionKey}
                      suggestion={translate(suggestionKey)}
                      onClick={handleSuggestionClick}
                      className="h-auto max-w-full whitespace-normal py-1.5 text-center hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
                    />
                  ))}
                </div>
              </ConversationEmptyState>
            )
          ) : (
            <div
              id="chat-response-area"
              className="mx-auto flex w-full max-w-6xl flex-col"
            >
              {/* Show mock question during tour */}
              {showMockContent && (
                <ChatMessageItem
                  message={{
                    role: "user",
                    content: translate(
                      AI_CHAT_MOCK_QUESTION_KEY as TranslationKeys,
                    ),
                  }}
                  index={0}
                />
              )}
              {/* Show mock thinking indicator during thinking step */}
              {isOnThinkingStep && (
                <div id="chat-thinking-indicator">
                  <StreamingMessage
                    message={{
                      role: "assistant",
                      content: translate("AI_CHAT/THINKING"),
                    }}
                  />
                </div>
              )}
              {/* Show mock response during response step */}
              {isOnResponseStep && (
                <ChatMessageItem
                  message={{
                    role: "assistant",
                    content: translate(
                      AI_CHAT_MOCK_RESPONSE_KEY as TranslationKeys,
                    ),
                  }}
                  index={1}
                />
              )}
              {/* Normal messages, interleaved with any proposal/action cards
                  anchored to that point in the thread (see
                  ProposalCard.anchorMessageCount) — a card renders right
                  after the turn that produced it, not after every later turn.
                  Agent-loop continuation messages carry raw tool_use/tool_result
                  blocks (toolContent) with no visible content and are never
                  rendered, so they're excluded before anchors are compared. */}
              {!showMockContent &&
                visibleMessages.map((message, index) => (
                  <React.Fragment key={index}>
                    {agentChatEnabled &&
                      anchoredPendingActions
                        .filter((action) => action.anchorSlot === index)
                        .map((action) => (
                          <div
                            key={action.id}
                            className="-mt-M mb-L flex flex-row gap-S"
                          >
                            <div className="w-10 shrink-0" />
                            <ActionMessage
                              label={action.label}
                              confirmed={action.status === "confirmed"}
                              onConfirm={() =>
                                void agentChat.confirmAction(action)
                              }
                            />
                          </div>
                        ))}
                    {agentChatEnabled &&
                      anchoredProposals
                        .filter((proposal) => proposal.anchorSlot === index)
                        .map((proposal) => (
                          <ProposalCard
                            key={proposal.id}
                            proposal={proposal}
                            onAccept={() =>
                              void agentChat.acceptProposal(proposal.id)
                            }
                            onReject={() =>
                              agentChat.rejectProposal(proposal.id)
                            }
                            className="my-XS"
                          />
                        ))}
                    <ChatMessageItem message={message} index={index} />
                  </React.Fragment>
                ))}
              {!showMockContent &&
                streamingMessage &&
                (streamingMessage.content ||
                  streamingMessage.reasoning !== undefined ||
                  (streamingMessage.blocks?.length ?? 0) > 0) && (
                  <div id="chat-thinking-indicator">
                    <StreamingMessage message={streamingMessage} />
                  </div>
                )}
              {!showMockContent && agentChatEnabled && (
                <>
                  {/* Cards anchored at or past the end of the current thread
                      (the common case: the turn that produced them is still
                      the latest one) render here, after everything else. */}
                  {anchoredPendingActions
                    .filter(
                      (action) => action.anchorSlot >= visibleMessages.length,
                    )
                    .map((action) => (
                      <div
                        key={action.id}
                        className="-mt-M mb-L flex flex-row gap-S"
                      >
                        <div className="w-10 shrink-0" />
                        <ActionMessage
                          label={action.label}
                          confirmed={action.status === "confirmed"}
                          onConfirm={() => void agentChat.confirmAction(action)}
                        />
                      </div>
                    ))}
                  {anchoredProposals
                    .filter(
                      (proposal) =>
                        proposal.anchorSlot >= visibleMessages.length,
                    )
                    .map((proposal) => (
                      <ProposalCard
                        key={proposal.id}
                        proposal={proposal}
                        onAccept={() =>
                          void agentChat.acceptProposal(proposal.id)
                        }
                        onReject={() => agentChat.rejectProposal(proposal.id)}
                        className="my-XS"
                      />
                    ))}
                  {/* The blocking Confirm / Cancel card is NOT rendered here:
                      it lives fused to the top of the composer (see
                      AgentConfirmationBar), which is what locks the input
                      while a tool call is suspended on the decision. */}
                </>
              )}
            </div>
          )}
        </ConversationContent>
        <QueueScrollSync queuedMessageCount={queuedMessages.length} />
        <ConfirmationScrollSync
          isAwaitingConfirmation={
            agentChatEnabled && !!agentChat.pendingConfirmation
          }
        />
        <ConversationScrollButton />
      </Conversation>

      <ChatInput
        compact={compact}
        prefill={inputPrefill}
        onSubmit={handleSubmit}
        isStreaming={isLoading || !!streamingMessage || isHydratingConversation}
        containerClassName={CHAT_SURFACE_WIDTH_CLASS}
        queuedMessages={queuedMessages}
        onRemoveQueuedMessage={removeQueuedMessage}
        onForceSendQueuedMessage={forceSendQueuedMessage}
        onCopy={copyToClipboard}
        onExport={exportChat}
        onExportPdf={exportChatPdf}
        onNewChat={startNewChat}
        hasMessages={!!chat.messages?.length}
        webSearchEnabled={webSearchEnabled}
        onToggleWebSearch={handleToggleWebSearch}
        agentChatEnabled={agentChatEnabled}
        stagedFiles={agentChat.stagedFiles}
        uploadingFiles={agentChat.uploadingFiles}
        onAttachFile={agentChat.attachFile}
        onRemoveStagedFile={agentChat.removeStagedFile}
        targetedAnswerLabel={agentChat.targetedAnswer?.questionLabel ?? null}
        onClearTargetedAnswer={agentChat.clearTargetedAnswer}
        targetedHelpArticleLabel={agentChat.targetedHelpArticle?.title ?? null}
        onClearTargetedHelpArticle={agentChat.clearTargetedHelpArticle}
        pendingConfirmation={agentChat.pendingConfirmation}
        onResolveConfirmation={agentChat.resolveConfirmation}
      />
    </div>
  )
}

export default ChatInterface
